import { useApi } from '@backstage/core-plugin-api';

import { scmAuthApiRef } from '@backstage/integration-react';
import { useEffect, useState } from 'react';
import useDebounce from 'react-use/lib/useDebounce';
import {
  FieldExtensionComponentProps,
  RepoUrlPickerUiOptions,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder';
import {
  DevHubAutocomplete,
  DevHubFormControl,
  DevHubFormHelperText,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { getRepos, useOrgsByLocation } from '../../services/github';
import { RepoUrlPickerHost } from './RepoUrlPickerHost';

import { serializeRepoPickerUrl } from '../../util/entityUtils';

type RequestUserCredentialsOptions = {
  secretsKey: string;
  enabled: boolean;
};

export interface RepoUrlPickerState {
  host?: string;
  owner?: string;
  repoName?: string;
  organization?: string;
  workspace?: string;
  project?: string;
  repoUrl?: string;
}

/**
 * The input props that can be specified under `ui:options` for the
 * `OwnedRepoUrlPicker` field extension.
 *
 * @public
 */

export type OwnedRepoPickerState = FieldExtensionComponentProps<
  string | undefined,
  RepoUrlPickerUiOptions
>;

/**
 * The underlying component that is rendered in the form for the `OwnedRepoUrlPicker`
 * field extension.
 * @param props - The properties passed to the component.
 * @public
 */
export const OwnedRepoUrlPicker = (props: OwnedRepoPickerState) => {
  const { uiSchema, onChange, rawErrors, idSchema } = props;
  const scmAuthApi = useApi(scmAuthApiRef);
  const allowedHosts = uiSchema?.['ui:options']?.allowedHosts ?? [
    'github.comcast.com',
  ];
  const [orgs, setOrgs] = useState<string[]>([]);
  const { value: orgsByUser, loading: orgsLoading } =
    useOrgsByLocation(allowedHosts);
  useEffect(() => {
    if (orgsByUser && orgsByUser?.size > 0) {
      setOrgs(orgsByUser?.get(allowedHosts[0]) ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgsByUser, orgsLoading]);

  const requestUserCredentials: RequestUserCredentialsOptions = (uiSchema[
    'ui:options'
  ]?.requestUserCredentials ?? {
    secretsKey: 'USER_OAUTH_TOKEN',
    enabled: true,
  }) as RequestUserCredentialsOptions;

  const { setSecrets } = useTemplateSecrets();

  const [state, setState] = useState<RepoUrlPickerState>({});

  const [allowedRepos, setRepoState] = useState<string[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [selectedRepo, setSelectedRepo] = useState<string>('');

  // Update the current owner onSelect
  // Update the repositories owned by the owner where the Discover app has been installed
  // Update the current repository
  const onSelect = async (_event: any, orgName: string | null) => {
    if (!orgName) {
      return;
    }
    setSelectedRepo('');
    const repos = await getRepos(orgName, state.host!);
    setRepoState(repos);
    setSelectedOwner(orgName);
    if (repos.length > 0) {
      setState(prevState => ({
        ...prevState,
        owner: orgName,
        repoName: allowedRepos![0],
      }));
    }
  };

  const onHostSelect = (host: string) => {
    setOrgs(orgsByUser?.get(host) ?? []);
    setState(prevState => ({ ...prevState, host }));
  };

  // Update the current repository
  const onRepoSelect = (_event: any, repoName: string | null) => {
    if (!repoName) {
      return;
    }
    setSelectedRepo(repoName);

    function parseRepoUrl(prevState: RepoUrlPickerState) {
      return (
        `${prevState.host ?? allowedHosts}?repo=${repoName}` +
        `&owner=${prevState.owner}&workspace=main`
      );
    }

    setState(prevState => ({
      ...prevState,
      repoName: repoName,
      repoUrl: parseRepoUrl(prevState),
    }));
  };

  // Update the return object state which is RepoPickerUrlState
  useEffect(() => {
    onChange(serializeRepoPickerUrl(state));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useDebounce(
    async () => {
      if (
        !requestUserCredentials ||
        !(state.host && state.owner && state.repoName)
      ) {
        return;
      }

      const [host, owner, repoName] = [
        state.host,
        state.owner,
        state.repoName,
      ].map(encodeURIComponent);

      // user has requested that we use the users credentials
      // so lets grab them using the scmAuthApi and pass through
      // any additional scopes from the ui:options
      const { token } = await scmAuthApi.getCredentials({
        url: `https://${host}/${owner}/${repoName}`,
        additionalScope: {
          repoWrite: true,
        },
      });

      // set the secret using the key provided in the ui:options for use
      // in the templating the manifest with ${{ secrets[secretsKey] }}
      setSecrets({
        [requestUserCredentials.secretsKey]: token,
      });
    },
    500,
    [state, uiSchema],
  );

  return (
    <>
      <RepoUrlPickerHost
        host={state.host}
        hosts={allowedHosts}
        onChange={onHostSelect}
        rawErrors={rawErrors}
      />

      <DevHubFormControl
        margin="normal"
        required
        error={rawErrors?.length > 0 && !state}
      >
        <DevHubAutocomplete
          id={idSchema?.$id}
          value={selectedOwner}
          loading={orgsLoading}
          options={orgs || []}
          onChange={onSelect}
          autoSelect
          renderInput={params => (
            <DevHubTextField
              {...params}
              label="Organizations available"
              margin="normal"
              helperText="Organizations available for the user"
              variant="outlined"
              required
              InputProps={params.InputProps}
            />
          )}
        />
        <DevHubFormHelperText>
          The organization, user or project that this repo will belong to
        </DevHubFormHelperText>
      </DevHubFormControl>
      <DevHubFormControl
        margin="normal"
        required
        error={rawErrors?.length > 0 && !state}
      >
        <DevHubAutocomplete
          id={idSchema?.$id}
          value={selectedRepo}
          options={allowedRepos || []}
          onChange={onRepoSelect}
          autoSelect
          renderInput={params => (
            <DevHubTextField
              {...params}
              label="Repositories Available"
              margin="normal"
              helperText="Repositories Available for the user"
              variant="outlined"
              required
              InputProps={params.InputProps}
            />
          )}
        />
        <DevHubFormHelperText>
          The organization, user or project that this repo will belong to
        </DevHubFormHelperText>
      </DevHubFormControl>
    </>
  );
};
